import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Trash2, Send, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DARK_INPUT = "rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40";

const imgSrc = (path) => (path?.startsWith("/") ? path : `${API}/files/${path}`);

export default function GalleryManager({ pin }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", event_date: "", story: "" });
  const [imageFiles, setImageFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/gallery`);
    setItems(res.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is over 5MB — skipped`);
      } else {
        valid.push(f);
      }
    }
    if (imageFiles.length + valid.length > 9) {
      toast.error("Up to 9 photos per event");
      return;
    }
    setImageFiles((prev) => [...prev, ...valid]);
    setPreviews((prev) => [...prev, ...valid.map((f) => URL.createObjectURL(f))]);
  };

  const removeImage = (idx) => {
    URL.revokeObjectURL(previews[idx]);
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearImages = () => {
    previews.forEach((p) => URL.revokeObjectURL(p));
    setImageFiles([]);
    setPreviews([]);
  };

  const publish = async () => {
    setSaving(true);
    try {
      const paths = [];
      for (const f of imageFiles) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("pin", pin);
        const up = await axios.post(`${API}/uploads`, fd);
        paths.push(up.data.path);
      }
      await axios.post(`${API}/gallery`, {
        ...form,
        image_path: paths[0],
        extra_images: paths.slice(1),
        pin,
      });
      toast.success("Added to the Fundraisers & Events gallery");
      setForm({ title: "", event_date: "", story: "" });
      clearImages();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item_id) => {
    try {
      await axios.post(`${API}/gallery/${item_id}/delete`, { pin });
      toast.success("Item deleted");
      await load();
    } catch {
      toast.error("Could not delete — please try again");
    }
  };

  return (
    <div className="mt-5 border border-white/15 bg-white/5 p-8" data-testid="gallery-manager">
      <div className="flex items-start gap-5">
        <div className="border border-white/20 p-3 text-brand-green">
          <Camera className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold">Fundraisers & events gallery</h3>
          <p className="mt-1 text-sm leading-relaxed text-white/60">
            Share photos and stories from fundraisers, in-store events and milestones. They appear instantly in the gallery.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-2" data-testid="gallery-controls">
        <div className="space-y-4">
          <Input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Title, e.g. Shave for a Cure fundraiser"
            data-testid="gallery-title-input"
            className={DARK_INPUT}
          />
          <Input
            value={form.event_date}
            onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
            placeholder="Date or occasion (optional), e.g. June 2026"
            data-testid="gallery-date-input"
            className={DARK_INPUT}
          />
          <Textarea
            value={form.story}
            onChange={(e) => setForm((f) => ({ ...f, story: e.target.value }))}
            placeholder="The story behind the photo (optional)…"
            rows={4}
            data-testid="gallery-story-input"
            className={DARK_INPUT}
          />
          <div>
            <label
              className="flex cursor-pointer items-center gap-3 border border-dashed border-white/25 px-4 py-3 text-sm text-white/60 transition-colors duration-200 hover:border-brand-green hover:text-white"
              data-testid="gallery-image-drop"
            >
              <ImagePlus className="h-5 w-5 shrink-0 text-brand-green" />
              <span className="truncate">
                {imageFiles.length ? `${imageFiles.length} photo${imageFiles.length > 1 ? "s" : ""} selected — first is the cover` : "Choose photos (required) — up to 9, JPG/PNG/WEBP, max 5MB each"}
              </span>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleFiles}
                data-testid="gallery-image-input"
              />
            </label>
            {previews.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="gallery-image-previews">
                {previews.map((src, i) => (
                  <div key={i} className="relative" data-testid={`gallery-preview-${i}`}>
                    <img src={src} alt={`Preview ${i + 1}`} className="h-16 w-24 rounded-lg object-cover" />
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">Cover</span>
                    )}
                    <button
                      onClick={() => removeImage(i)}
                      data-testid={`gallery-remove-${i}`}
                      aria-label={`Remove photo ${i + 1}`}
                      className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white transition-colors duration-200 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            onClick={publish}
            disabled={saving || !form.title.trim() || imageFiles.length === 0}
            data-testid="gallery-publish-btn"
            className="rounded-none bg-brand-green px-6 font-heading font-semibold text-white hover:bg-brand-greendark disabled:opacity-50"
          >
            <Send className="mr-2 h-4 w-4" /> {saving ? "Publishing…" : "Add to gallery"}
          </Button>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/50">Gallery items</p>
          <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="gallery-items-list">
            {items.map((item) => (
              <li key={item.item_id} className="flex items-center justify-between gap-3 border border-white/10 bg-white/5 px-4 py-3" data-testid={`gallery-item-${item.item_id}`}>
                <img src={imgSrc(item.image_path)} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{item.title}</p>
                  <p className="text-xs text-white/40">{item.event_date || "No date"}</p>
                </div>
                <button
                  onClick={() => remove(item.item_id)}
                  data-testid={`gallery-delete-${item.item_id}`}
                  aria-label={`Delete ${item.title}`}
                  className="shrink-0 p-2 text-white/40 transition-colors duration-200 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
